import { apiClient } from './apiClient';
import type {
  BulkDeletePublishersDto,
  BulkPublisherOperationResultDto,
  BulkPublisherQuotaUpdateDto,
  BulkSetPublisherActiveDto,
  CreatePublisherManageDto,
  ManagePublishersListResult,
  PublisherFilterState,
  PublisherManageDto,
  UpdatePublisherManageDto,
} from '../types/managePublishers';

const BASE = '/api/app/publishers-extn/manage';

function toQuery(f: PublisherFilterState): Record<string, string | number | boolean> {
  const q: Record<string, string | number | boolean> = {
    skipCount: (f.page - 1) * f.pageSize,
    maxResultCount: f.pageSize,
    sorting: `${f.sort ?? 'name'} ${f.dir ?? 'asc'}`,
  };
  if (f.q) q.filterText = f.q;
  if (f.isActive !== undefined) q.isActive = f.isActive;
  if (f.isDrmEnabled !== undefined) q.isDrmEnabled = f.isDrmEnabled;
  if (f.createdAfter) q.createdAfter = f.createdAfter;
  if (f.createdBefore) q.createdBefore = f.createdBefore;
  if (f.bookCountMin !== undefined) q.bookCountMin = f.bookCountMin;
  if (f.bookCountMax !== undefined) q.bookCountMax = f.bookCountMax;
  if (f.includeDeleted) q.includeDeleted = true;
  return q;
}

export const publishersManageService = {
  list: async (filters: PublisherFilterState): Promise<ManagePublishersListResult> => {
    const { data } = await apiClient.get<ManagePublishersListResult>(`${BASE}/list`, {
      params: toQuery(filters),
    });
    return data;
  },

  idsForFilter: async (filters: PublisherFilterState): Promise<string[]> => {
    const { data } = await apiClient.get<string[]>(`${BASE}/ids-for-filter`, {
      params: toQuery(filters),
    });
    return data;
  },

  affectedBookCount: async (publisherIds: string[]): Promise<number> => {
    const { data } = await apiClient.post<number>(`${BASE}/affected-book-count`, publisherIds);
    return data;
  },

  get: async (id: string): Promise<PublisherManageDto> => {
    const { data } = await apiClient.get<PublisherManageDto>(`${BASE}/${id}`);
    return data;
  },

  create: async (input: CreatePublisherManageDto): Promise<PublisherManageDto> => {
    const { data } = await apiClient.post<PublisherManageDto>(BASE, input);
    return data;
  },

  update: async (id: string, input: UpdatePublisherManageDto): Promise<PublisherManageDto> => {
    const { data } = await apiClient.put<PublisherManageDto>(`${BASE}/${id}`, input);
    return data;
  },

  delete: async (id: string, alsoDeleteBooks: boolean): Promise<void> => {
    await apiClient.delete(`${BASE}/${id}`, { params: { alsoDeleteBooks } });
  },

  restore: async (id: string, alsoRestoreBooks: boolean): Promise<PublisherManageDto> => {
    const { data } = await apiClient.post<PublisherManageDto>(
      `${BASE}/${id}/restore`,
      null,
      { params: { alsoRestoreBooks } }
    );
    return data;
  },

  bulkSetActive: async (input: BulkSetPublisherActiveDto): Promise<BulkPublisherOperationResultDto> => {
    const { data } = await apiClient.post<BulkPublisherOperationResultDto>(`${BASE}/bulk/set-active`, input);
    return data;
  },

  bulkQuotas: async (input: BulkPublisherQuotaUpdateDto): Promise<BulkPublisherOperationResultDto> => {
    const { data } = await apiClient.post<BulkPublisherOperationResultDto>(`${BASE}/bulk/quotas`, input);
    return data;
  },

  bulkDelete: async (input: BulkDeletePublishersDto): Promise<BulkPublisherOperationResultDto> => {
    const { data } = await apiClient.post<BulkPublisherOperationResultDto>(`${BASE}/bulk/delete`, input);
    return data;
  },

  exportExcel: async (filters: PublisherFilterState): Promise<Blob> => {
    const body = {
      skipCount: 0,
      maxResultCount: 10000,
      sorting: `${filters.sort ?? 'name'} ${filters.dir ?? 'asc'}`,
      filterText: filters.q,
      isActive: filters.isActive,
      isDrmEnabled: filters.isDrmEnabled,
      createdAfter: filters.createdAfter,
      createdBefore: filters.createdBefore,
      bookCountMin: filters.bookCountMin,
      bookCountMax: filters.bookCountMax,
      includeDeleted: filters.includeDeleted ?? false,
    };
    const { data } = await apiClient.post(`${BASE}/export-excel`, body, {
      responseType: 'blob',
    });
    return data as Blob;
  },
};
